import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, merge, of } from "rxjs";
import { filter, take } from "rxjs/operators";
import jwtDecode from "jwt-decode";
import { CacheService } from "../cache.service";
import { AccessLevel } from "@dentally/enums/access-level";
import { LambdaRequest } from "@apis/_core/types/LambdaRequest";
import { LocationService } from "../location.service";
import { Constants } from "@apps/patient-core/constants";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { E_Referrer } from "@backend/common/enums/referrer";
import { WindowService } from "../window.service";

dayjs.extend(advancedFormat);

export const PUBLIC_IN_PRACTICE_JWT_STORAGE_KEY = "public_in_practice_jwt";

@Injectable()
export abstract class BaseJWTService {
  public onJWTChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  public onInvalidJWT: Subject<void> = new Subject<void>();
  public onSessionIdChanged: Subject<string> = new Subject<string>();

  private _internalJwtParsed: Record<string, any> | null = null;

  constructor(protected _cacheService: CacheService, protected _windowService: WindowService, protected _locationService: LocationService) {}

  public abstract handleInvalidToken(reload?: boolean): null;

  public abstract get isRestricted(): boolean;

  // Required to make the service compatible with the new concierge auth
  public init(): Promise<void> {
    return Promise.resolve();
  }

  protected get _jwtParsed(): Record<string, any> | null {
    if (this._internalJwtParsed) {
      const timestamp = new Date().getTime() / 1000;

      if (timestamp > this._internalJwtParsed.exp || this._internalJwtParsed?.version !== "3.0.0") {
        this._internalJwtParsed = null;
        throw new Error("JWT expired");
      }

      if (this._internalJwtParsed.payment_plan_id) {
        this._internalJwtParsed.payment_plan_id = Number(this._internalJwtParsed.payment_plan_id);
      }
    }
    return this._internalJwtParsed;
  }

  protected set _jwtParsed(jwtParsed: Record<string, any> | null) {
    this._internalJwtParsed = jwtParsed;
  }

  protected get _jwtParseNoExpCheck(): Record<string, any> | null {
    return this._internalJwtParsed;
  }

  public get jwtExpiryFormatted(): string | null {
    const expiry = this.getJwtField("exp");
    if (!expiry) return null;

    return dayjs(parseInt(expiry) * 1000).format("Do MMMM YYYY HH:mm:ss");
  }

  public get jwtCreatedAtFormatted(): string | null {
    const createdAt = this.getJwtField("iat");
    if (!createdAt) return null;

    return dayjs(parseInt(createdAt) * 1000).format("Do MMMM YYYY HH:mm:ss");
  }

  public get accessLevel(): AccessLevel {
    try {
      const parsedJWT = this._getParsedJWT();

      if (parsedJWT?.access_level) {
        return parsedJWT.access_level;
      }
    } catch {
      // This should only happen for the unit tests
    }

    return AccessLevel.PUBLIC;
  }

  public get hasExpired(): boolean {
    const jwt = this._jwtParseNoExpCheck;

    if (!jwt) return false;

    return jwt.exp < new Date().getTime() / 1000;
  }

  public get referrerAsJson(): { type: E_Referrer } {
    const { referrer: referrerJSON } = this.getJWT();
    const referrer = referrerJSON ? JSON.parse(referrerJSON) : { type: E_Referrer.ORGANIC };
    return referrer;
  }

  public get sid(): string {
    return this.getJwtSafe()?.sid ?? "";
  }

  public isConcierge(): boolean {
    return LambdaRequest.conciergeAccessLevels.includes(this._jwtParseNoExpCheck?.access_level);
  }

  public isPatientInPractice() {
    return this._jwtParseNoExpCheck?.access_level === AccessLevel.PATIENT_IN_PRACTICE;
  }

  public isPublicInPractice(accessLevel: AccessLevel): boolean {
    return accessLevel === AccessLevel.PUBLIC_IN_PRACTICE;
  }

  public isPublicAccess() {
    return LambdaRequest.publicAccessLevels.includes(this._jwtParseNoExpCheck?.access_level);
  }

  public isPatientUnauthenticated() {
    try {
      return [AccessLevel.PATIENT_UNAUTHENTICATED, AccessLevel.PATIENT_IN_PRACTICE].includes(this.getJWT("access_level"));
    } catch {
      this.handleInvalidToken(true);
      return false;
    }
  }

  public isPublic() {
    return this._jwtParseNoExpCheck?.access_level === AccessLevel.PUBLIC;
  }

  public isImpersonating() {
    return this.getJWT()?.user_impersonating_patient !== undefined;
  }

  public isLoggedIn() {
    try {
      return this.getJWT("access_level") === AccessLevel.PATIENT;
    } catch {
      this.handleInvalidToken(true);
      return false;
    }
  }

  public restorePublicInPracticeToken() {
    const publicInPracticeJwt = this._cacheService.get(PUBLIC_IN_PRACTICE_JWT_STORAGE_KEY);
    if (publicInPracticeJwt) this.setToken(publicInPracticeJwt);
  }

  public isPatient() {
    return this.isLoggedIn() || this.isPatientUnauthenticated();
  }

  public get canLogin(): boolean {
    return !["false", false].includes(this.getJWT("can_login"));
  }

  public getFirstName(): any {
    const patient_first_name = this.getJWT("patient_first_name");

    if (patient_first_name) {
      return `${patient_first_name}'s`;
    }
    return "Your";
  }

  protected _getParsedJWT() {
    if (!this._jwtParsed) {
      const jwt = this.getJWTString();
      if (jwt) this.setToken(jwt);
    }
    return this._jwtParsed;
  }

  public setPublicToken(jwt: string, forceSwap = false) {
    const jwtCurrent = forceSwap ? null : this.getJWT(null, false);
    if (jwtCurrent === null) {
      this.setToken(jwt);
    }
  }

  public getJwtField(field: string): string | null {
    return this.getJwtSafe()?.[field] ?? null;
  }

  /**
   * Return the JWT if it has not yet expired, otherwise it will return null
   */
  public getJwtSafe(): Record<string, string> | null {
    try {
      return this._getParsedJWT();
    } catch (e) {
      return null;
    }
  }

  public getJWT(field?: string | null, reloadOnExpired = true): any {
    let parsedJWT: any;

    try {
      parsedJWT = this._getParsedJWT();
    } catch (error) {
      console.error("Failed to parse JWT", { error });

      return this.handleInvalidToken(reloadOnExpired);
    }
    if (parsedJWT) {
      if (field && parsedJWT[field] !== undefined) return parsedJWT[field];
      return parsedJWT;
    }

    return null;
  }

  public getJWTString(): string {
    return this._cacheService.get("jwt") ?? "";
  }

  public delete() {
    this._internalJwtParsed = null;
    this._cacheService.delete("jwt");
  }

  public setToken(jwt: string) {
    const existing = this._getExistingToken();
    this._jwtParsed = this._parseJwt(jwt);
    this._cacheService.set("jwt", jwt);
    this.onJWTChanged.next({ status: "UPDATED", jwt: this._jwtParsed });

    if (existing?.sid !== this._jwtParsed?.sid) {
      this.onSessionIdChanged.next(this._jwtParsed?.sid);
    }
  }

  private _getExistingToken(): Record<string, any> {
    const jwt = this.getJWTString();

    if (!jwt) return {};

    return this._parseJwt(jwt) || {};
  }

  /**
   * Waits for the JWT to be available so that API calls that require it can be delayed
   */
  public waitForJwt(): Observable<any> {
    return merge(this.onJWTChanged, of({ jwt: this.getJWT() })).pipe(
      filter((status) => status && status.jwt),
      take(1)
    );
  }

  private _parseJwt(token): Record<string, any> | null {
    const jwt = jwtDecode(token);
    if (!jwt) return null;
    return jwt;
  }

  public get hasNoJwt(): boolean {
    return !this._cacheService.get(Constants.JWT_STORAGE_KEY);
  }

  public getAuthToken(): Observable<string | null> {
    return of(this.getJWTString());
  }
}
